import { rem } from 'polished'
import React, { useContext } from 'react'
import { connect } from 'react-redux'
import styled from 'styled-components'

import { accountAccessShowLogin } from 'actions/UiActions'
import HeightFillingImage from 'components/Common/HeightFillingImage'
import LoadingAnimation from 'components/Common/Loading/LoadingAnimation'
import WalledContent from 'components/Common/WalledContent'
import TextButton from 'components/Luxkit/Button/TextButton'
import PriceTagIcon from 'components/Luxkit/Icons/custom/PriceTagIcon'
import LineMapMarkerIcon from 'components/Luxkit/Icons/line/LineMapMarkerIcon'
import ProductTypeLabel from 'components/Luxkit/Label/ProductTypeLabel'
import TextLink from 'components/Luxkit/TextLink'
import BodyText from 'components/Luxkit/Typography/BodyText'
import SoldOutMessage from 'components/OfferList/OfferListTiles/Support/SoldOutMessage'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { OFFER_TYPE_BED_BANK, OFFER_TYPE_TOUR } from 'constants/offer'
import useOffersBestPrices from 'hooks/useOfferBestPrices'
import shouldHideAoLabel from 'lib/hideAoLabel'
import { isBedbank } from 'lib/offerTypes'

import IconButton from 'components/Luxkit/Button/IconButton'
import LineTimesIcon from 'components/Luxkit/Icons/line/LineTimesIcon'
import BodyTextBlock from 'components/Luxkit/TextBlocks/BodyTextBlock'
import CaptionTextBlock from 'components/Luxkit/TextBlocks/CaptionTextBlock'
import { GlobalSearchStateContext } from 'contexts/GlobalSearch/GlobalSearchContexts'
import { useBedbankSearchTilePromotions } from 'hooks/OfferPage/useBedbankPromotions'
import { useAppDispatch } from 'hooks/reduxHooks'
import useBedbankRates from 'hooks/useBedbankRates'
import { useIsMobileScreen } from 'lib/deviceUtils'
import { buildSuggestedDatesParamsKey } from 'lib/searchUtils'
import { getSuggestedDates } from 'selectors/offerSelectors'
import MapViewBedbankPricing from './MapViewBedbankPricing'
import MapViewOfferTilePricing from './MapViewOfferTilePricing'

export const DESKTOP_TILE_WIDTH = 600
export const DESKTOP_TILE_IMAGE_WIDTH = 240

const Root = styled.div`
  position: relative;
  pointer-events: all;
  background-color: ${({ theme }) => theme.palette.neutral.eight};
  display: flex;
  flex-direction: column;
  width: calc(100vw - 48px); /* Mobile-only sizing, no need to scale with rems */
  max-width: 360px;
  min-height: ${rem(348)};
  border-radius: ${props => props.theme.borderRadius.medium};
  box-shadow: 0px 0px 8px rgba(0, 0, 0, 0.15);

  ${mediaQueryUp.sm} {
    flex-direction: row;
    padding: ${rem(8)};
    width: ${rem(DESKTOP_TILE_WIDTH)};
    max-width: unset;
    min-height: ${rem(184)};
  }
`

const ImageWrapper = styled.div`
  ${mediaQueryUp.sm}{
    width: ${rem(DESKTOP_TILE_IMAGE_WIDTH)};
  }
`

const StyledImage = styled(HeightFillingImage)`
  border-radius: ${props => props.theme.borderRadius.medium} ${props => props.theme.borderRadius.medium} 0 0;
  overflow: hidden;

  ${mediaQueryUp.sm}{
    border-radius: ${props => props.theme.borderRadius.medium};
  }
`

const Section = styled.div`
  padding: ${rem(16)};
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  ${mediaQueryUp.sm} {
    padding: ${rem(12)} ${rem(16)} ${rem(12)} ${rem(16)};
  }
`

const TopSection = styled.div`
  & > * + * {
    margin-top: ${rem(8)};
  }
`

const BottomSection = styled.div`
  margin-top: ${rem(16)};
  text-align: right;

  & > * + * {
    margin-top: ${rem(4)};
  }

  ${mediaQueryUp.sm} {
    margin-top: ${rem(20)};
  }
`

const TitleText = styled(BodyText)`
  width: 100%;
`

// TEXT TODO: confirm with design
const ErrorBodyText = styled(BodyText)`
  color: ${props => props.theme.palette.messaging.critical};
`

const ButtonsContainer = styled.div`
  & > * + * {
    margin-left: ${rem(8)};
  }
`

const Loader = styled(LoadingAnimation)`
  color: ${({ theme }) => theme.palette.primary.normal};
`

const StyledIconButton = styled(IconButton)`
  position: absolute;
  right: 0;
  top: 0;
`

const imageParams = {
  xs: '3:2',
  xsWidth: '100vw',
  sm: '6:5',
  smWidth: '240px',
  md: '10:7',
  mdWidth: '240px',
}

const imageMinHeightAspectRatio = {
  xs: '3:2',
  sm: '6:5',
  md: '10:7',
}

interface MappedStateProps {
  suggestedDates: App.OfferSuggestedDates
  windowSearch: string
}

export interface Props {
  className?: string;
  offer: App.Offer | App.OfferSummary | App.BedbankOffer | App.BedbankOfferSummary;
  filters?: App.OfferListFilters;
  onClose?: () => void;
}

function MapViewOfferTile(props: Props & MappedStateProps) {
  const {
    className,
    offer,
    filters,
    suggestedDates,
    windowSearch,
    onClose,
  } = props

  const {
    name,
    type,
  } = offer

  const dispatch = useAppDispatch()

  const filtersToSearch = {
    ...filters,
    checkIn: filters.checkIn ?? suggestedDates?.checkIn,
    checkOut: filters.checkOut ?? suggestedDates?.checkOut,
  }

  const {
    bestPrice,
    duration,
    loading: priceLoading,
    error: priceError,
    soldOut,
    bestPricePackage,
  } = useOffersBestPrices(offer, filtersToSearch)

  const hasDates = !!(filters?.checkIn && filters?.checkOut)

  const isMobileScreen = useIsMobileScreen()

  const hasSuggestedDates = !!(suggestedDates?.checkIn && suggestedDates?.checkOut)

  const [hotelRates] = useBedbankRates(offer.id, filters?.rooms, filters?.checkIn, filters?.checkOut, offer.type === OFFER_TYPE_BED_BANK)
  const rate = hotelRates[0]
  const isLTS = !!useBedbankSearchTilePromotions('promotions' in offer && offer.type === OFFER_TYPE_BED_BANK ? offer.promotions : [], rate, filtersToSearch.checkIn, filtersToSearch.checkOut).length

  let location = ''
  if (isBedbank(offer)) {
    location = [offer.property.address.city, offer.property.address.countryName].join(', ')
  } else {
    location = [offer.locationHeading, offer.locationSubheading].filter(t => t).join(', ')
  }
  const header = isBedbank(offer) || OFFER_TYPE_TOUR === offer.type ? name : offer.property.name

  const { onBedbankSelectDatesPrompt } = useContext(GlobalSearchStateContext)

  const loginHandler: React.MouseEventHandler<HTMLButtonElement> = (e) => {
    e.preventDefault()
    dispatch(accountAccessShowLogin())
  }

  const onSelectDatesPrompt: React.MouseEventHandler<HTMLAnchorElement> = (e) => {
    e.preventDefault()
    onBedbankSelectDatesPrompt?.(offer.id, offer.type, windowSearch)
  }

  let bottomContent: JSX.Element

  if (!hasDates && !hasSuggestedDates && isBedbank(offer)) {
    bottomContent = <TextLink size="caption" onClick={onSelectDatesPrompt}>Select dates to view rates</TextLink>
  }
  else if (priceLoading) {
    bottomContent = <Loader/>
  }
  else if (priceError) {
    bottomContent = <ErrorBodyText variant="large">Error fetching price</ErrorBodyText>
  }
  else if (soldOut) {
    bottomContent = <SoldOutMessage offer={offer} filters={filters}/>
  }
  else if (isBedbank(offer)) {
    bottomContent = <MapViewBedbankPricing
      filters={filters}
      offer={offer}
      price={bestPrice}
      duration={duration}
    />
  }
  else {
    bottomContent = <MapViewOfferTilePricing
      offer={offer}
      bestPricePackage={bestPricePackage}
      filters={filters}
      price={bestPrice}
      duration={duration}
    />
  }

  return (
    <Root className={className}>
      <ImageWrapper>
        <StyledImage
          {...imageParams}
          minHeightAspectRatio={imageMinHeightAspectRatio}
          id={offer.image.id}
          alt={header}
          loading="eager"
          fit="right"
        />
      </ImageWrapper>
      <Section>
        {onClose && <StyledIconButton
          kind={isMobileScreen ? 'primary' : 'tertiary'}
          onClick={(e) => {
            e.preventDefault()
            onClose()
          }}
        >
          <LineTimesIcon />
        </StyledIconButton>}
        <TopSection>
          <CaptionTextBlock variant="large" weight="bold" startIcon={<LineMapMarkerIcon />}>
            {location}
          </CaptionTextBlock>
          <TitleText variant="large" weight="bold" lineClamp={2}>{header}</TitleText>
          {!shouldHideAoLabel(offer) && <ProductTypeLabel className={type} productType={isLTS ? 'limited_time_special' : offer.productType} />}
        </TopSection>
        <BottomSection>
          <WalledContent enforced={offer.walledGarden} wall={(
            <>
              <BodyTextBlock align="end" variant="medium" weight="semi-bold" startIcon={<PriceTagIcon />}>
                Login or Sign up for free to see member rates
              </BodyTextBlock>
              <ButtonsContainer>
                <TextButton kind="secondary" onClick={loginHandler}>Log In</TextButton>
                <TextButton kind="primary" onClick={loginHandler}>Sign up for free</TextButton>
              </ButtonsContainer>
            </>
          )}>
            {bottomContent}
          </WalledContent>
        </BottomSection>
      </Section>
    </Root>
  )
}

export default connect<MappedStateProps, undefined, Props, App.State>((appState, ownProps) => {
  const flexibleSearchFilterKey = buildSuggestedDatesParamsKey(ownProps.filters?.flexibleMonths, ownProps.filters?.flexibleNights, ownProps.filters?.rooms)
  return {
    suggestedDates: getSuggestedDates(appState, flexibleSearchFilterKey, ownProps.offer.id),
    windowSearch: appState.router.location.search,
  }
})(MapViewOfferTile)
